/**
 * Server-side SEO data for dynamic routes.
 *
 * The page components render their tags through react-helmet-async, which only
 * runs after hydration. Crawlers and social scrapers read the server HTML, so
 * these loaders feed the route-level head() with the same facts.
 */
import { supabase } from "@/integrations/supabase/client";
import { formatCardTitle, formatCardDescription } from "@/lib/seoHelpers";

export const SITE_ORIGIN = "https://www.kreditkarten.de";

export interface CardSeo {
  slug: string;
  title: string;
  description: string;
  image?: string;
  issuer?: string;
  annualFee?: number;
  updatedAt?: string;
}

export interface ArticleSeo {
  slug: string;
  title: string;
  description: string;
  image?: string;
  publishedAt?: string;
  updatedAt?: string;
  author?: string;
}

const absolute = (url?: string | null) =>
  url && /^https?:\/\//i.test(url) ? url : undefined;

export async function loadCardSeo(slug: string): Promise<CardSeo | null> {
  try {
    const { data } = await supabase
      .from("cards")
      .select(
        "slug, name, display_name, issuer, excerpt, annual_fee, apr_effective, logo_url, updated_at",
      )
      .eq("slug", slug)
      .eq("status", "published")
      .maybeSingle();

    if (!data) return null;
    const card = data as Record<string, any>;
    const name = card.display_name || card.name;

    return {
      slug: card.slug,
      title: formatCardTitle(name, card.apr_effective ?? undefined, card.annual_fee ?? undefined),
      description: formatCardDescription(
        card.excerpt || "Kreditkarte mit attraktiven Vorteilen",
        name,
      ),
      image: absolute(card.logo_url),
      issuer: card.issuer ?? undefined,
      annualFee: typeof card.annual_fee === "number" ? card.annual_fee : undefined,
      updatedAt: card.updated_at ?? undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Everything the card route needs on the server: the SEO facts, the full card
 * row so the page renders real content in the server HTML, and the target of a
 * legacy-slug redirect when the slug no longer exists.
 */
export async function loadCardPage(slug: string): Promise<{
  seo: CardSeo | null;
  card: Record<string, any> | null;
  redirectSlug: string | null;
}> {
  try {
    const { data } = await supabase
      .from("cards")
      .select("*")
      .eq("slug", slug)
      .eq("status", "published")
      .maybeSingle();

    if (data) {
      const card = data as Record<string, any>;
      const name = card.display_name || card.name;
      return {
        card,
        redirectSlug: null,
        seo: {
          slug: card.slug,
          title: formatCardTitle(
            name,
            card.apr_effective ?? undefined,
            card.annual_fee ?? undefined,
          ),
          description: formatCardDescription(
            card.excerpt || "Kreditkarte mit attraktiven Vorteilen",
            name,
          ),
          image: absolute(card.logo_url),
          issuer: card.issuer ?? undefined,
          annualFee: typeof card.annual_fee === "number" ? card.annual_fee : undefined,
          updatedAt: card.updated_at ?? undefined,
        },
      };
    }

    // Legacy slug? Resolve it server-side so crawlers get a real redirect.
    const { data: redirect } = await supabase
      .from("card_slug_redirects")
      .select("card_id")
      .eq("old_slug", slug)
      .maybeSingle();

    if (redirect?.card_id) {
      const { data: target } = await supabase
        .from("cards")
        .select("slug")
        .eq("id", redirect.card_id)
        .eq("status", "published")
        .maybeSingle();
      if (target?.slug) return { seo: null, card: null, redirectSlug: target.slug };
    }

    return { seo: null, card: null, redirectSlug: null };
  } catch {
    return { seo: null, card: null, redirectSlug: null };
  }
}

export async function loadArticleSeo(slug: string): Promise<ArticleSeo | null> {
  try {
    const { data } = await supabase
      .from("articles")
      .select(
        "slug, title, meta_title, meta_description, excerpt, og_image_url, featured_image_url, published_at, updated_at, author",
      )
      .eq("slug", slug)
      .eq("status", "published")
      .maybeSingle();

    if (!data) return null;
    const article = data as Record<string, any>;

    return {
      slug: article.slug,
      title: article.meta_title || article.title,
      description: article.meta_description || article.excerpt || article.title,
      image: absolute(article.og_image_url || article.featured_image_url),
      publishedAt: article.published_at ?? undefined,
      updatedAt: article.updated_at ?? undefined,
      author: article.author ?? undefined,
    };
  } catch {
    return null;
  }
}

/** Meta list for a page we could not resolve — keeps it out of the index. */
export const unresolvedMeta = [
  { title: "Seite nicht gefunden – Kreditkarten.de" },
  { name: "robots", content: "noindex, follow" },
];
